import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import gpay from "./gpay.png";

/* ─── SAME CONTACT / WAITLIST THEME TOKENS ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#188038",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#81c995",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root {
    min-height: 100%;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    background: ${t.bg};
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes popIn {
    from { opacity: 0; transform: scale(0.96) translateY(8px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }

  .fade-up { animation: fadeUp 0.25s ease forwards; }
  .pop-in { animation: popIn 0.35s ease forwards; }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${t.textSub};
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
    flex-shrink: 0;
  }

  .icon-btn:hover {
    background: ${t.surface};
    color: ${t.text};
  }

  .field {
    width: 100%;
    background: ${t.inputBg};
    border: 1px solid ${t.inputBorder};
    color: ${t.text};
    border-radius: 14px;
    padding: 15px 16px;
    font-size: 14.5px;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    outline: none;
    transition: border-color 0.12s, background 0.12s;
    min-height: 52px;
  }

  .field:focus {
    border-color: ${t.borderStrong};
  }

  .field::placeholder {
    color: ${t.textMuted};
  }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 12px;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.12s, transform 0.12s;
    font-family: 'Google Sans', sans-serif;
    width: 100%;
  }

  .submit-btn:hover:not(:disabled) {
    opacity: .88;
    transform: translateY(-1px);
  }

  .submit-btn:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${t.border};
    border-radius: 12px;
    background: ${t.bg};
    color: ${t.text};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, border-color 0.12s;
    text-decoration: none;
  }

  .ghost-btn:hover {
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }

  .material-icons-round {
    font-size: 20px;
    user-select: none;
    line-height: 1;
  }

  .footer {
    margin-top: 24px;
    background: ${t.bgSub};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .footer-inner {
    padding: 24px;
  }

  .footer-top {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr 1fr 1fr;
    gap: 24px;
    margin-bottom: 22px;
  }

  .footer-logo {
    font-size: 22px;
    font-weight: 800;
    color: ${t.text};
    margin-bottom: 10px;
  }

  .footer-logo span {
    color: ${t.textSub};
  }

  .footer-brand p {
    margin: 0;
    font-size: 13px;
    color: ${t.textSub};
    line-height: 1.7;
  }

  .footer-col h4 {
    margin: 0 0 14px;
    font-size: 13px;
    font-weight: 700;
    color: ${t.text};
  }

  .footer-col a {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: ${t.textSub};
    text-decoration: none;
    transition: color 0.12s ease;
  }

  .footer-col a:hover {
    color: ${t.text};
  }

  .footer-divider {
    height: 1px;
    background: ${t.border};
    margin-bottom: 16px;
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .footer-bottom span {
    font-size: 12px;
    color: ${t.textMuted};
  }

  @media (max-width: 768px) {
    .footer {
      border-radius: 16px;
    }

    .footer-top {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 520px) {
    .footer-top {
      grid-template-columns: 1fr;
    }
  }
`;

export default function Donate() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);

  const [currentStep, setCurrentStep] = useState(1);
  const [donorName, setDonorName] = useState(localStorage.getItem("userName") || "");
  const [donationAmount, setDonationAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const T = dark ? THEMES.dark : THEMES.light;

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const styleId = "omrali-donate-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  const handleNameSubmit = (e) => {
    e.preventDefault();
    setErr("");

    const amount = parseFloat(donationAmount);

    if (!donorName.trim()) {
      setErr("Please enter your name.");
      return;
    }

    if (!donationAmount || isNaN(amount) || amount <= 0) {
      setErr("Please enter a valid donation amount.");
      return;
    }

    setCurrentStep(2);
  };

  const handlePaymentDone = async () => {
    setErr("");
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "donations"), {
        donorName: donorName.trim(),
        donationAmount: parseFloat(donationAmount),
        timestamp: serverTimestamp(),
        status: "completed",
      });

      setCurrentStep(3);
    } catch (error) {
      console.error("Error saving donation:", error);
      setErr("There was an error processing your donation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: T.accent,
          color: T.accentText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          fontFamily: "'Google Sans', sans-serif",
          flexShrink: 0,
        }}
      >
        O
      </div>
      <span
        style={{
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: "'Google Sans', sans-serif",
          letterSpacing: "-0.3px",
        }}
      >
        Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
      </span>
    </div>
  );

  const stepCircle = (step) => ({
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: currentStep >= step ? T.accent : T.surface,
    color: currentStep >= step ? T.accentText : T.textSub,
    border: `1px solid ${currentStep >= step ? T.accent : T.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  });

  const stepLine = (active) => ({
    flex: 1,
    height: 2,
    borderRadius: 999,
    background: active ? T.accent : T.border,
    margin: "0 10px",
  });

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: T.textSub,
    marginBottom: 7,
    paddingLeft: 2,
    textTransform: "uppercase",
    letterSpacing: ".04em",
  };

  const renderStep1 = () => (
    <div
      className="pop-in"
      style={{
        display: "grid",
        gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
        gap: 18,
      }}
    >
      {/* Left info */}
      <div
        style={{
          background: T.bgSub,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: mobile ? "16px" : "22px",
          boxShadow: T.shadow,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>
          Why support Omrali AI?
        </div>

        <p
          style={{
            color: T.textSub,
            fontSize: 14,
            lineHeight: 1.8,
            marginBottom: 18,
          }}
        >
          Your donation helps us improve the platform, ship better AI experiences,
          and keep building faster for users and businesses.
        </p>

        <div style={{ display: "grid", gap: 12 }}>
          {[
            {
              title: "Support product development",
              desc: "Help us build better features, improve performance, and launch faster.",
            },
            {
              title: "Fund future releases",
              desc: "Contributions directly help with scaling, infra, and future model improvements.",
            },
            {
              title: "Become a visible supporter",
              desc: "You’ll be listed on the Omrali AI supporters page after successful donation.",
            },
          ].map((item) => (
            <div
              key={item.title}
              style={{
                border: `1px solid ${T.border}`,
                background: T.bg,
                borderRadius: 14,
                padding: "14px 14px",
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: T.accent,
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: T.text,
                      marginBottom: 4,
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: T.textSub,
                      lineHeight: 1.6,
                    }}
                  >
                    {item.desc}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleNameSubmit}
        style={{
          background: T.bgSub,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: mobile ? "16px" : "22px",
          boxShadow: T.shadow,
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
          Enter your donation details
        </div>

        <p
          style={{
            color: T.textSub,
            fontSize: 13.5,
            lineHeight: 1.7,
          }}
        >
          Add your name and donation amount to continue to payment.
        </p>

        <div>
          <div style={labelStyle}>Full Name</div>
          <input
            className="field"
            type="text"
            placeholder="Enter your full name"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
          />
        </div>

        <div>
          <div style={labelStyle}>Donation Amount</div>
          <div style={{ position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: T.textSub,
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              ₹
            </span>
            <input
              className="field"
              type="number"
              min="1"
              step="0.01"
              placeholder="Enter donation amount"
              value={donationAmount}
              onChange={(e) => setDonationAmount(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
        </div>

        {err && (
          <div
            style={{
              background: `${T.danger}18`,
              color: T.danger,
              border: `1px solid ${T.danger}33`,
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 13.5,
              lineHeight: 1.5,
            }}
          >
            {err}
          </div>
        )}

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          <Icon n="payments" />
          Continue to Payment
        </button>

        <div
          style={{
            border: `1px solid ${T.border}`,
            background: T.bg,
            borderRadius: 14,
            padding: "12px 14px",
            color: T.textSub,
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
        >
          After you complete payment, tap <strong>“I’ve Completed the Payment”</strong> so we can add your contribution to the supporters list.
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div
      className="pop-in"
      style={{
        display: "grid",
        gridTemplateColumns: mobile ? "1fr" : "0.95fr 1.05fr",
        gap: 18,
      }}
    >
      {/* Summary */}
      <div
        style={{
          background: T.bgSub,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: mobile ? "16px" : "22px",
          boxShadow: T.shadow,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>
          Donation summary
        </div>

        <div
          style={{
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            padding: "14px",
            marginBottom: 14,
          }}
        >
          <div style={{ color: T.textSub, fontSize: 12, marginBottom: 6 }}>Donor</div>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 600 }}>{donorName}</div>
        </div>

        <div
          style={{
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            padding: "14px",
            marginBottom: 14,
          }}
        >
          <div style={{ color: T.textSub, fontSize: 12, marginBottom: 6 }}>Amount</div>
          <div style={{ color: T.text, fontSize: 22, fontWeight: 700 }}>₹{donationAmount}</div>
        </div>

        <div
          style={{
            border: `1px solid ${T.border}`,
            background: T.aiBubble,
            borderRadius: 14,
            padding: "14px",
            color: T.textSub,
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          Scan the QR using GPay, complete the payment, then confirm below.
        </div>
      </div>

      {/* QR + button */}
      <div
        style={{
          background: T.bgSub,
          border: `1px solid ${T.border}`,
          borderRadius: 18,
          padding: mobile ? "16px" : "22px",
          boxShadow: T.shadow,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>
          Complete your payment
        </div>

        <div
          style={{
            background: T.bg,
            border: `1px solid ${T.border}`,
            borderRadius: 18,
            padding: mobile ? "18px" : "24px",
            marginBottom: 16,
          }}
        >
          <img
            src={gpay}
            alt="GPay QR"
            style={{
              width: "100%",
              maxWidth: 260,
              borderRadius: 14,
              display: "block",
              margin: "0 auto 14px",
            }}
          />

          <div
            style={{
              color: T.textSub,
              fontSize: 13,
              marginBottom: 10,
            }}
          >
            Scan this code with Google Pay
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px 16px",
              borderRadius: 999,
              background: T.accent,
              color: T.accentText,
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            ₹{donationAmount}
          </div>
        </div>

        {err && (
          <div
            style={{
              background: `${T.danger}18`,
              color: T.danger,
              border: `1px solid ${T.danger}33`,
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: 13.5,
              lineHeight: 1.5,
              marginBottom: 12,
              textAlign: "left",
            }}
          >
            {err}
          </div>
        )}

        <button
          onClick={handlePaymentDone}
          className="submit-btn"
          disabled={isSubmitting}
          style={{ marginBottom: 12 }}
        >
          <Icon n={isSubmitting ? "hourglass_empty" : "verified"} />
          {isSubmitting ? "Saving..." : "I've Completed the Payment"}
        </button>

        <button
          onClick={() => setCurrentStep(1)}
          className="ghost-btn"
          style={{ width: "100%" }}
        >
          <Icon n="arrow_back" />
          Edit details
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div
      className="pop-in"
      style={{
        maxWidth: 760,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: T.aiBubble,
          border: `1px solid ${T.aiBorder}`,
          borderRadius: 18,
          padding: mobile ? "20px" : "28px",
          boxShadow: T.shadow,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 74,
            height: 74,
            borderRadius: "50%",
            margin: "0 auto 18px",
            background: `${T.success}18`,
            border: `1px solid ${T.success}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.success,
          }}
        >
          <Icon n="check_circle" style={{ fontSize: 40 }} />
        </div>

        <h2
          style={{
            fontSize: mobile ? 26 : 34,
            lineHeight: 1.15,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: T.text,
            marginBottom: 12,
          }}
        >
          Thank you, {donorName}!
        </h2>

        <p
          style={{
            color: T.textSub,
            fontSize: 15,
            lineHeight: 1.8,
            maxWidth: 640,
            margin: "0 auto 12px",
          }}
        >
          Your generous donation of <strong style={{ color: T.text }}>₹{donationAmount}</strong> means a lot to us and helps us continue building Omrali AI.
        </p>

        <p
          style={{
            color: T.textSub,
            fontSize: 14,
            lineHeight: 1.8,
            maxWidth: 640,
            margin: "0 auto 22px",
          }}
        >
          You’ll be added to the supporters list on Omrali AI. You can view it later at{" "}
          <strong style={{ color: T.text }}>omrali.com/donorlist</strong>.
        </p>

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <a href="/" className="ghost-btn">
            <Icon n="home" />
            Go Home
          </a>

          <a href="/contact" className="submit-btn" style={{ width: "auto" }}>
            <Icon n="mail" />
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: mobile ? "10px 14px" : "10px 20px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg,
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {currentStep < 3 && (
            <button className="icon-btn" onClick={() => navigate(-1)} title="Back">
              <Icon n="arrow_back" />
            </button>
          )}
          {currentStep === 3 && (
            <button className="icon-btn" onClick={() => navigate("/")} title="Home">
              <Icon n="home" />
            </button>
          )}
          <Logo />
        </div>

        <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
          <Icon n={dark ? "light_mode" : "dark_mode"} />
        </button>
      </header>

      <main
        style={{
          maxWidth: 1040,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 24px 36px",
        }}
      >
        <div className="fade-up" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Hero */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "24px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: T.accent,
                  display: "inline-block",
                }}
              />
              Support Omrali AI
            </div>

            <h1
              style={{
                fontSize: mobile ? 28 : 38,
                lineHeight: 1.1,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: T.text,
                marginBottom: 12,
              }}
            >
              Donate to help us build faster
            </h1>

            <p
              style={{
                color: T.textSub,
                fontSize: 14.5,
                lineHeight: 1.8,
                maxWidth: 760,
                marginBottom: 18,
              }}
            >
              Support the future of Omrali AI. Your contribution helps us improve the product,
              scale infrastructure, and build better experiences for everyone.
            </p>

            {/* Progress */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                maxWidth: 460,
              }}
            >
              <div style={stepCircle(1)}>1</div>
              <div style={stepLine(currentStep > 1)} />
              <div style={stepCircle(2)}>2</div>
              <div style={stepLine(currentStep > 2)} />
              <div style={stepCircle(3)}>3</div>
            </div>
          </div>

          {/* Dynamic step */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Footer */}
          <div className="footer fade-up">
            <div className="footer-inner">
              <div className="footer-top">
                <div className="footer-brand">
                  <div className="footer-logo">
                    Omrali <span>AI</span>
                  </div>
                  <p>
                    Next-generation conversational AI for businesses and developers.
                    Built with care, designed to scale.
                  </p>
                </div>

                <div className="footer-col">
                  <h4>Product</h4>
                  <a href="https://chat.omrali.com">Chat</a>
                </div>

                <div className="footer-col">
                  <h4>Company</h4>
                  <a href="/about">About us</a>
                  <a href="/waitlist">Beta</a>
                  <a href="/contact">Contact</a>
                </div>

                <div className="footer-col">
                  <h4>Legal</h4>
                  <a href="/privacy">Privacy policy</a>
                  <a href="/terms">Terms of service</a>
                </div>

                <div className="footer-col">
                  <h4>Social</h4>
                  <a href="https://www.instagram.com/omrali.ai/">Instagram</a>
                  <a href="https://twitter.com/omrali_ai">Twitter</a>
                </div>
              </div>

              <div className="footer-divider" />

              <div className="footer-bottom">
                <span>© 2026 Omrali AI. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}